import React from "react";
import { Music2 } from "lucide-react";
import { artSrc } from "@/components/riptag/util";

const chip = (t) => {
  if (t.in_library) return ["In Library", "bg-brand-cyan/15 text-brand-cyan border-brand-cyan/40"];
  if (t.dup_status === "flagged") return ["Possible dup", "bg-amber-500/15 text-amber-300 border-amber-500/40"];
  if (t.verify_status === "error") return ["Corrupt?", "bg-rose-500/15 text-rose-300 border-rose-500/40"];
  if (t.verify_status === "warning") return ["Check audio", "bg-amber-500/15 text-amber-300 border-amber-500/40"];
  if (t.status === "tagged") return ["Tagged", "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"];
  if (t.verify_status === "ok") return ["Verified", "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"];
  return ["Uploaded", "bg-bg-elevated text-ink-secondary border-line"];
};

export const ArtThumb = ({ url, size = "w-9 h-9", testId }) => (
  <span className={`relative ${size} shrink-0 rounded-md overflow-hidden border border-line bg-bg-elevated flex items-center justify-center`} data-testid={testId}>
    <Music2 className="h-4 w-4 opacity-30" />
    {url && (
      <img src={artSrc(url)} alt="" loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        className="absolute inset-0 w-full h-full object-cover" />
    )}
  </span>
);

export const TrackTable = ({ tracks, selectedId, onSelect, checked = [], onCheck }) => {
  if (!tracks.length) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-8 text-center text-sm text-ink-muted" data-testid="rip-track-table-empty">
        <Music2 className="h-6 w-6 mx-auto mb-2 opacity-50" />
        No tracks yet — upload some rips above.
      </div>
    );
  }
  const allChecked = checked.length > 0 && checked.length === tracks.length;
  const toggleAll = () => onCheck?.(allChecked ? [] : tracks.map((t) => t.track_id));
  const toggle = (id) =>
    onCheck?.(checked.includes(id) ? checked.filter((c) => c !== id) : [...checked, id]);

  return (
    <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="rip-track-table">
      <div className="px-4 py-2.5 border-b border-line text-xs font-mono uppercase tracking-wider text-ink-muted flex items-center gap-3">
        {onCheck && (
          <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="rip-check-all"
            className="h-3.5 w-3.5 accent-amber-500 cursor-pointer" />
        )}
        Tracks ({tracks.length})
      </div>
      <div className="divide-y divide-line max-h-[480px] overflow-y-auto">
        {tracks.map((t, i) => {
          const [label, cls] = chip(t);
          return (
            <div key={t.track_id} className={`flex items-center gap-3 px-4 py-2.5 transition hover:bg-bg-elevated ${
              selectedId === t.track_id ? "bg-bg-elevated border-l-2 border-brand-amber" : ""}`}>
              {onCheck && (
                <input type="checkbox" checked={checked.includes(t.track_id)} onChange={() => toggle(t.track_id)}
                  data-testid={`rip-check-${t.track_id}`}
                  className="h-3.5 w-3.5 shrink-0 accent-amber-500 cursor-pointer" />
              )}
              <button onClick={() => onSelect(t.track_id)}
                data-testid={`rip-track-row-${t.track_id}`}
                className="flex-1 flex items-center gap-3 text-left min-w-0">
                <span className="w-6 shrink-0 text-xs font-mono text-ink-muted">
                  {String(t.track_no || i + 1).padStart(2, "0")}
                </span>
                <ArtThumb url={t.art_url} testId={`rip-track-art-${t.track_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.title || t.filename}</div>
                  <div className="text-[11px] text-ink-muted font-mono truncate">
                    {t.artist || "Unknown artist"}{t.album ? ` · ${t.album}` : ""} · {Math.floor((t.duration || 0) / 60)}:{String(Math.round((t.duration || 0) % 60)).padStart(2, "0")} · {(t.format || "").toUpperCase()}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label}</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
